import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toLocalYMD } from "@/utils/dateUtils";
import { useAuthStore } from "@/stores/useAuthStore";
import { captureEntityMutation } from "@/analytics/posthog";

interface BudgetBalance {
  id: number;
  name: string;
  amount: number;
  category_id?: number;
  spent_amount: number;
  remaining_amount: number;
  description?: string | null;
}

interface BudgetData {
  total_budgeted: number;
  total_spent: number;
  remaining_budget: number;
  budgets_balance: BudgetBalance[];
}

interface CreateBudgetInput {
  category_id: number;
  amount: number;
  description?: string | null;
}

interface UpdateBudgetInput {
  category_id?: number;
  amount?: number;
  description?: string | null;
}

interface BudgetBalanceQueryParams {
  billable_date_gte?: string;
  billable_date_lte?: string;
  amount?: "asc" | "desc" | string;
}

const getEffectiveBudgetParams = (params?: BudgetBalanceQueryParams): Required<BudgetBalanceQueryParams> => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    billable_date_gte: params?.billable_date_gte ?? toLocalYMD(startOfMonth),
    billable_date_lte: params?.billable_date_lte ?? toLocalYMD(endOfMonth),
    amount: params?.amount ?? "desc",
  };
};

const fetchBudgetBalance = async (params: Required<BudgetBalanceQueryParams>): Promise<BudgetData> => {
  const { data } = await apiClient.get("/budgets/balance", {
    params,
  });
  return data;
};

export const useBudget = (params?: BudgetBalanceQueryParams, options?: { enabled?: boolean }) => {
  const effectiveParams = getEffectiveBudgetParams(params);
  const token = useAuthStore((s) => s.token);

  return useQuery<BudgetData, Error>({
    queryKey: ["budgetBalance", effectiveParams],
    queryFn: () => fetchBudgetBalance(effectiveParams),
    enabled: !!token && (options?.enabled ?? true),
  });
};

export const useCreateBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBudgetInput) => {
      const res = await apiClient.post("/budgets/", payload);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "budget", operation: "create", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["budgetBalance"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "budget", operation: "create", result: "failure", error });
    },
  });
};

export const useUpdateBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateBudgetInput }) => {
      const res = await apiClient.patch(`/budgets/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "budget", operation: "update", result: "success" });
      queryClient.invalidateQueries({ queryKey: ["budgetBalance"] });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "budget", operation: "update", result: "failure", error });
    },
  });
};

export const useDeleteBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiClient.delete(`/budgets/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["budgetBalance"] });
    },
  });
};
